import MatchGameEnum from 'models/MatchGameEnum';
import { __ } from 'i18n/index';
import { isDoublesMatch, opponentsOf, teammatesOf, teamNameOf, playerOf } from 'react/match/rankedPanel/matchTeams';
import loadedCharacterOf from 'react/match/rankedPanel/loadedCharacterOf';
import describeLobby, { isTeamLobby } from 'react/match/lobby/describeLobby';

// What the ranked panel's step line and the match status line say: which
// step the match is on (`kind`), whose move it is (`turn`: mine, theirs,
// alert or done; `playing` for friendlies, where nobody waits on anyone), a
// title and one sentence of why. A match that is not a set has one step,
// `friendlies`, with no score and no game counter.
//
// Doubles: either teammate acts for the team (the first click counts for
// stage steps; the last vote stands for reports), both players on a team
// pick a character, and a lobby the host hasn't started is the `lobby` step
// (react/match/lobby/describeLobby).

const STRIKE_ACTIONS = { 1: MatchGameEnum.ACTION_PLAYER_1_STRIKE_STAGE, 2: MatchGameEnum.ACTION_PLAYER_2_STRIKE_STAGE };
const BAN_ACTIONS = { 1: MatchGameEnum.ACTION_PLAYER_1_BAN_STAGE, 2: MatchGameEnum.ACTION_PLAYER_2_BAN_STAGE };
const STAGE_PICK_ACTIONS = { 1: MatchGameEnum.ACTION_PLAYER_1_PICK_STAGE, 2: MatchGameEnum.ACTION_PLAYER_2_PICK_STAGE };
const CHARACTER_PICK_ACTIONS = { 1: MatchGameEnum.ACTION_PLAYER_1_PICK_CHARACTER, 2: MatchGameEnum.ACTION_PLAYER_2_PICK_CHARACTER };

function opponentNameOf(match) {
	return teamNameOf(opponentsOf(match), __('your opponent'));
}

// The names a doubles step uses: the other team, my teammate, and "You or
// Lucky" for who can act for us.
function doublesNamesOf(match) {
	const partner = teammatesOf(match)[0] || null;
	const partnerName = partner ? partner.username : __('your teammate');
	return { partner, partnerName, eitherOfUs: __('You or %s', partnerName) };
}

function stagesWord(count) {
	return count === 1 ? __('Strike 1 stage') : __('Strike %s stages', count);
}

function bansWord(count) {
	return count === 1 ? __('Ban 1 stage') : __('Ban %s stages', count);
}

function iWonPreviousGame(match, myTeam) {
	return !!match.previous_game && match.previous_game.teamWon(myTeam);
}

// Doubles: the host gets Rehost + Reinvite; everyone else is told so.
function rehostNoteOf(match) {
	const host = match.player1;
	if(!isDoublesMatch(match) || !host || String(host.id) === String(myUser?.id)) {
		return '';
	}
	return __('%s hosted. If they rehost, you get an invite.', host.username);
}

function overStep(match) {
	const detail = rehostNoteOf(match);
	if(match.is_cancelled) {
		return { kind: 'over', turn: 'done', title: __('Set cancelled'), detail };
	}
	const mine = match.getMyTeamWinCount() || 0;
	const theirs = match.getOtherTeamWinCount() || 0;
	const score = `${mine}–${theirs}`;
	if(mine === theirs) {
		return { kind: 'over', turn: 'done', title: __('Set over %s', score), detail };
	}
	if(isDoublesMatch(match)) {
		return { kind: 'over', turn: 'done', title: mine > theirs ? __('Your team won %s', score) : __('Your team lost %s', score), detail };
	}
	return { kind: 'over', turn: 'done', title: mine > theirs ? __('You won %s', score) : __('You lost %s', score), detail };
}

const isResult = (report) => report === WIN_MATCH || report === LOSE_MATCH;

function gameReports(game, myTeam, otherTeam) {
	return {
		mine: game.teams?.[myTeam]?.getMatchReportValueToRepresentMyDecision() ?? null,
		theirs: game.teams?.[otherTeam]?.match_report ?? null,
	};
}

// The server disputes a set as soon as the two votes name different winners.
// Until someone asks to cancel or calls a moderator, either player can still
// fix it by changing their vote, so the panel keeps the winner buttons.
export function isVoteConflict(match) {
	if(!match.disputeIsActive()) {
		return false;
	}
	const requests = Object.values(match.player_report || {});
	if(requests.some((request) => request && (request.reported_cancel || request.reported_dispute))) {
		return false;
	}
	const game = match.game;
	if(!game || game.current_action !== MatchGameEnum.ACTION_PLAYERS_PLAY_GAME) {
		return false;
	}
	const { mine, theirs } = gameReports(game, match.getMyTeamNumber(), match.getOtherTeamNumber());
	return isResult(mine) && isResult(theirs) && mine === theirs;
}

// Who cast a team's vote, if the server says (teams[n].reported_by_player_id).
function voterOf(match, game, teamNumber) {
	return playerOf(match, game.teams?.[teamNumber]?.reported_by_player_id);
}

function doublesReportStep(match, game, myTeam, otherTeam, opponentName) {
	const number = game.game_number;
	const { mine, theirs } = gameReports(game, myTeam, otherTeam);
	const { eitherOfUs } = doublesNamesOf(match);
	if(!isResult(mine)) {
		return { kind: 'report', turn: 'mine', title: __('Who won game %s?', number), detail: __('One vote per team, from %s. It counts once both teams agree.', eitherOfUs.charAt(0).toLowerCase() + eitherOfUs.slice(1)) };
	}
	const myVoter = voterOf(match, game, myTeam);
	const isMyVote = myVoter && String(myVoter.id) === String(myUser?.id);
	const ourVoterName = myVoter ? (isMyVote ? __('You') : myVoter.username) : __('Your team');
	const ourPick = mine === WIN_MATCH ? __('your team') : opponentName;
	if(isResult(theirs) && theirs === mine) {
		const theirVoter = voterOf(match, game, otherTeam);
		const theirPick = theirs === WIN_MATCH ? __('their team') : __('your team');
		return {
			kind: 'conflict',
			turn: 'alert',
			title: __('Your teams reported different winners'),
			detail: __('%s said %s won, %s said %s won.', ourVoterName, ourPick, theirVoter ? theirVoter.username : opponentName, theirPick),
		};
	}
	const opponents = opponentsOf(match).map((player) => player.username);
	const eitherOfThem = opponents.length === 2 ? __('%s or %s', opponents[0], opponents[1]) : opponentName;
	return {
		kind: 'report',
		turn: 'theirs',
		title: __('Waiting for %s to report', opponentName),
		detail: !myVoter && mine === WIN_MATCH
			? __('Your team reported a win. It counts once %s agrees.', eitherOfThem)
			: __('%s said %s won. It counts once %s agrees.', ourVoterName, ourPick, eitherOfThem),
	};
}

function reportStep(game, myTeam, otherTeam, opponentName) {
	const number = game.game_number;
	const { mine, theirs } = gameReports(game, myTeam, otherTeam);

	if(!isResult(mine)) {
		return { kind: 'report', turn: 'mine', title: __('Who won game %s?', number), detail: __('You both report. It counts once you agree.') };
	}
	const mineNamed = mine === WIN_MATCH ? __('you') : opponentName;
	if(isResult(theirs) && theirs === mine) {
		const theirsNamed = theirs === WIN_MATCH ? opponentName : __('you');
		return {
			kind: 'conflict',
			turn: 'alert',
			title: __('You reported different winners'),
			detail: __('You said %s won, %s said %s won.', mineNamed, opponentName, theirsNamed),
		};
	}
	return { kind: 'report', turn: 'theirs', title: __('Waiting for %s to report', opponentName), detail: __('You said %s won. It counts once you agree.', mineNamed) };
}

function playerNamed(match, playerId) {
	const entry = Object.values(match.players || {}).find((matchPlayer) => matchPlayer.player?.id === playerId);
	return entry ? entry.player.username : null;
}

// "Moonwalk struck Battlefield. " (bans from game 2 on), or '' when unknown.
function lastStrikeSentence(match, game, stagesById) {
	const strike = game.last_strike;
	const stage = strike && stagesById ? stagesById.get(strike.stage_id) || stagesById.get(String(strike.stage_id)) : null;
	const name = strike ? playerNamed(match, strike.player_id) : null;
	if(!stage || !name) {
		return '';
	}
	return (game.game_number > 1 ? __('%s banned %s.', name, stage.name) : __('%s struck %s.', name, stage.name)) + ' ';
}

// stagesById: the season's stages (Season.getStages()), used to name the
// last strike; optional.
export default function describeRankedStep(match, stagesById = null) {
	const step = describeStepOf(match);
	if(['strike', 'ban', 'counterpick'].includes(step.kind) && match.game) {
		return { ...step, detail: lastStrikeSentence(match, match.game, stagesById) + step.detail };
	}
	return step;
}

function friendliesStep(match, opponentName) {
	if(match.is_cancelled || match.isEnded()) {
		return { kind: 'over', turn: 'done', title: __('Friendlies over'), detail: rehostNoteOf(match) };
	}
	const detail = isDoublesMatch(match)
		? __('Play as many games as you like. I\'m done playing ends it for everyone.')
		: __('Play as many games as you like.');
	return { kind: 'friendlies', turn: 'playing', title: __('Friendlies vs %s', opponentName), detail };
}

function hasPicked(game, player) {
	return !!player && !!game.players?.[player.id]?.character;
}

// Blind picks in doubles: my teammate's pick shows to me (the server shows
// teammates each other's picks); the other team's shows once all four are in.
function doublesBlindPickStep(match, game, opponentName) {
	const { partner, partnerName } = doublesNamesOf(match);
	if(hasPicked(game, myUser)) {
		if(partner && !hasPicked(game, partner)) {
			return { kind: 'pick', turn: 'theirs', title: __('Waiting for %s to pick', partnerName), detail: __('All four picks show once everyone has chosen.') };
		}
		return { kind: 'pick', turn: 'theirs', title: __('Waiting for %s to pick', opponentName), detail: __('All four picks show once everyone has chosen.') };
	}
	const partnerCharacter = loadedCharacterOf(game, partner);
	const partnerSentence = partnerCharacter ? __('%s picked %s.', partnerName, partnerCharacter.name) + ' ' : '';
	return { kind: 'pick', turn: 'mine', title: __('Pick your character'), detail: partnerSentence + __('%s see your team\'s picks once all four are in.', opponentName) };
}

// Counterpick characters in doubles: both players on the picking team pick.
function doublesCharacterPickStep(match, game, myTeam, otherTeam, opponentName, myTeamPicks) {
	const number = game.game_number;
	const { partner, partnerName } = doublesNamesOf(match);
	const weWonLast = iWonPreviousGame(match, myTeam);
	if(myTeamPicks) {
		const order = weWonLast
			? __('Your team won game %s, so you pick first and %s see it.', number - 1, opponentName)
			: __('%s won game %s and picked first.', opponentName, number - 1);
		if(hasPicked(game, myUser)) {
			return { kind: 'pick', turn: 'theirs', title: __('Waiting for %s to pick', partnerName), detail: order + ' ' + __('Game %s starts once %s picks.', number, partnerName) };
		}
		return { kind: 'pick', turn: 'mine', title: __('Pick your character'), detail: order };
	}
	const detail = weWonLast
		? __('Your team picked first. They see it before they pick.')
		: __('%s won game %s, so they pick first.', opponentName, number - 1);
	return { kind: 'pick', turn: 'theirs', title: __('Waiting for %s to pick', opponentName), detail };
}

function describeDoublesStageStep(match, action, myTeam, otherTeam, opponentName) {
	const number = match.game.game_number;
	const { eitherOfUs } = doublesNamesOf(match);
	if(action === STRIKE_ACTIONS[myTeam]) {
		return { kind: 'strike', turn: 'mine', title: stagesWord(match.getStrikesRemainingCount()), detail: __('%s can strike; the first click counts. Game %s is played on the last stage left.', eitherOfUs, number) };
	}
	if(action === STRIKE_ACTIONS[otherTeam]) {
		return { kind: 'strike', turn: 'theirs', title: __('Waiting for %s to strike', opponentName), detail: __('Either of them strikes for their team. Game %s is played on the last stage left.', number) };
	}
	if(action === BAN_ACTIONS[myTeam]) {
		return { kind: 'ban', turn: 'mine', title: bansWord(match.getStrikesRemainingCount()), detail: __('Your team won game %s. %s bans; the first click counts. Then %s pick game %s\'s stage.', number - 1, eitherOfUs, opponentName, number) };
	}
	if(action === BAN_ACTIONS[otherTeam]) {
		return { kind: 'ban', turn: 'theirs', title: __('Waiting for %s to ban stages', opponentName), detail: __('Then your team picks game %s\'s stage.', number) };
	}
	if(action === STAGE_PICK_ACTIONS[myTeam]) {
		return { kind: 'counterpick', turn: 'mine', title: __('Pick the stage for game %s', number), detail: __('%s won game %s, so your team picks. %s; the first click counts.', opponentName, number - 1, eitherOfUs) };
	}
	if(action === STAGE_PICK_ACTIONS[otherTeam]) {
		return { kind: 'counterpick', turn: 'theirs', title: __('%s are picking the stage', opponentName), detail: __('Your team won game %s, so they choose.', number - 1) };
	}
	return null;
}

function describeDoublesGameStep(match, game, myTeam, otherTeam, opponentName) {
	const action = game.current_action;
	if(action === MatchGameEnum.ACTION_PLAYERS_BLIND_PICK_CHARACTERS) {
		return doublesBlindPickStep(match, game, opponentName);
	}
	const stageStep = describeDoublesStageStep(match, action, myTeam, otherTeam, opponentName);
	if(stageStep) {
		return stageStep;
	}
	if(action === CHARACTER_PICK_ACTIONS[myTeam] || action === CHARACTER_PICK_ACTIONS[otherTeam]) {
		return doublesCharacterPickStep(match, game, myTeam, otherTeam, opponentName, action === CHARACTER_PICK_ACTIONS[myTeam]);
	}
	if(action === MatchGameEnum.ACTION_PLAYERS_PLAY_GAME) {
		return doublesReportStep(match, game, myTeam, otherTeam, opponentName);
	}
	if(action === MatchGameEnum.ACTION_GAME_OVER) {
		return overStep(match);
	}
	return { kind: 'none', turn: 'theirs', title: '', detail: '' };
}

function describeStepOf(match) {
	if(isTeamLobby(match)) {
		return describeLobby(match).step;
	}
	const opponentName = opponentNameOf(match);
	const doubles = isDoublesMatch(match);
	if(!match.isASet()) {
		return friendliesStep(match, opponentName);
	}
	const myTeam = match.getMyTeamNumber();
	const otherTeam = match.getOtherTeamNumber();

	// Match.isEnded() also covers a disputed set, which is still being settled.
	if(match.is_cancelled || (match.isEnded() && !match.disputeIsActive())) {
		return overStep(match);
	}
	if(match.player_report?.[myTeam]?.reported_cancel) {
		// The server records who pressed Cancel as the team's voter.
		const asker = doubles && match.game ? voterOf(match, match.game, myTeam) : null;
		const askerName = asker ? (String(asker.id) === String(myUser?.id) ? __('You') : asker.username) : __('Your team');
		const detail = doubles
			? __('%s asked to cancel. If %s agree, the set doesn\'t count.', askerName, opponentName)
			: __('If you both agree, the set doesn\'t count.');
		return { kind: 'cancel', turn: 'theirs', title: __('Waiting for %s to agree to cancel', opponentName), detail };
	}
	// Their cancel request disputes the set, but it's mine to answer before a
	// moderator is needed.
	const myRequest = match.player_report?.[myTeam];
	if(match.player_report?.[otherTeam]?.reported_cancel && !myRequest?.reported_dispute) {
		return { kind: 'cancel', turn: 'mine', title: __('%s asked to cancel', opponentName), detail: __('Agree and the set doesn\'t count, or dispute it and a moderator decides.') };
	}
	if(match.disputeIsActive() && !isVoteConflict(match)) {
		const detail = doubles
			? __('Each of you can add what happened below so they can decide.')
			: __('Add what happened below so they can decide.');
		return { kind: 'disputed', turn: 'alert', title: __('A moderator will review this set'), detail };
	}

	const game = match.game;
	if(!game) {
		return { kind: 'none', turn: 'theirs', title: '', detail: '' };
	}
	if(doubles) {
		return describeDoublesGameStep(match, game, myTeam, otherTeam, opponentName);
	}
	const number = game.game_number;
	const action = game.current_action;

	if(action === MatchGameEnum.ACTION_PLAYERS_BLIND_PICK_CHARACTERS) {
		if(game.players?.[myUser.id]?.character) {
			return { kind: 'pick', turn: 'theirs', title: __('Waiting for %s to pick', opponentName), detail: __('Both picks show once you\'ve both chosen.') };
		}
		const opponents = match.getOtherPlayers();
		const opponentsPicked = opponents.length > 0 && opponents.every((player) => game.players?.[player.id]?.character);
		if(opponentsPicked) {
			return { kind: 'pick', turn: 'mine', title: __('Pick your character'), detail: __('%s has picked. Both picks show once you choose.', opponentName) };
		}
		return { kind: 'pick', turn: 'mine', title: __('Pick your character'), detail: __('Hidden until %s picks too.', opponentName) };
	}
	if(action === STRIKE_ACTIONS[myTeam] || action === STRIKE_ACTIONS[otherTeam]) {
		const detail = __('Game %s is played on the last stage left.', number);
		return action === STRIKE_ACTIONS[myTeam]
			? { kind: 'strike', turn: 'mine', title: stagesWord(match.getStrikesRemainingCount()), detail }
			: { kind: 'strike', turn: 'theirs', title: __('Waiting for %s to strike', opponentName), detail };
	}
	if(action === BAN_ACTIONS[myTeam]) {
		return { kind: 'ban', turn: 'mine', title: bansWord(match.getStrikesRemainingCount()), detail: __('You won game %s. %s picks game %s\'s stage from what\'s left.', number - 1, opponentName, number) };
	}
	if(action === BAN_ACTIONS[otherTeam]) {
		return { kind: 'ban', turn: 'theirs', title: __('Waiting for %s to ban stages', opponentName), detail: __('Then you pick game %s\'s stage.', number) };
	}
	if(action === STAGE_PICK_ACTIONS[myTeam]) {
		return { kind: 'counterpick', turn: 'mine', title: __('Pick the stage for game %s', number), detail: __('%s won game %s, so you choose.', opponentName, number - 1) };
	}
	if(action === STAGE_PICK_ACTIONS[otherTeam]) {
		return { kind: 'counterpick', turn: 'theirs', title: __('%s is picking the stage', opponentName), detail: __('You won game %s, so they choose.', number - 1) };
	}
	if(action === CHARACTER_PICK_ACTIONS[myTeam]) {
		const detail = iWonPreviousGame(match, myTeam)
			? __('You won game %s, so you pick first and %s sees it.', number - 1, opponentName)
			: __('%s picked first. Your pick is the last one.', opponentName);
		return { kind: 'pick', turn: 'mine', title: __('Pick your character'), detail };
	}
	if(action === CHARACTER_PICK_ACTIONS[otherTeam]) {
		const detail = iWonPreviousGame(match, myTeam)
			? __('You picked first. They see it before they pick.')
			: __('%s won game %s, so they pick first.', opponentName, number - 1);
		return { kind: 'pick', turn: 'theirs', title: __('Waiting for %s to pick', opponentName), detail };
	}
	if(action === MatchGameEnum.ACTION_PLAYERS_PLAY_GAME) {
		return reportStep(game, myTeam, otherTeam, opponentName);
	}
	if(action === MatchGameEnum.ACTION_GAME_OVER) {
		return overStep(match);
	}
	return { kind: 'none', turn: 'theirs', title: '', detail: '' };
}
